import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTransactionsHandler, clearTransactionsHandler } from "../redux/handlers";
import AdminLayout from "../../../components/Shared/AdminLayout"
import Container from "../../../components/Shared/Container"
import TransactionCard from "../../../components/Admin/Transactions/TransactionCard"
import Pagination from "../../../components/Shared/Pagination"

const TRANSACTION_TYPES = [
    { value: "", label: "ALL TYPES" },
    { value: "zave_reward_deposit", label: "ZAVE REWARD" },
    { value: "upi_instant_refund", label: "UPI REFUND" },
    { value: "upi_instant_deposit", label: "UPI DEPOSIT" },
    { value: "voucher_card_settlement", label: "VOUCHER SETTLEMENT" }
];

const Transactions = () => {
    const dispatch = useDispatch();
    const { transactions, transactionsLoading, transactionsTotal } = useSelector(state => state.admin);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchDebounce, setSearchDebounce] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    
    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch transactions when search debounce or page changes
    useEffect(() => {
        const params = { 
            page: currentPage, 
            size: itemsPerPage 
        };
        
        if (selectedType) {
            params.type = selectedType;
        }
        
        if (searchDebounce.trim()) {
            params.search = searchDebounce.trim();
        }
        
        dispatch(fetchTransactionsHandler(params));
    }, [searchDebounce, currentPage, selectedType, itemsPerPage, dispatch]);

    const handleSearch = (value) => {
        setSearchQuery(value);
        setCurrentPage(1);
    };
    
    // Helper to detect search type for better UX feedback
    const getSearchType = (query) => {
        if (!query) return '';
        if (query.startsWith('pay_')) return 'Payment Reference';
        if (query.startsWith('ZAVE91')) return 'Account ID';
        if (query.includes('@upi')) return 'UPI ID';
        if (/^\d{10}$/.test(query)) return 'Phone Number';
        return 'Search';
    };

    const handleTypeChange = (type) => {
        setSelectedType(type);
        setIsDropdownOpen(false);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const getTypeColor = (type) => {
        switch(type) {
            case "zave_reward_deposit":
                return "text-green-500";
            case "upi_instant_refund":
                return "text-blue-500";
            case "upi_instant_deposit":
                return "text-purple-500";
            case "voucher_card_settlement":
                return "text-yellow-500";
            default:
                return "text-white";
        }
    };

    // Calculate total pages
    const totalPages = Math.ceil(transactionsTotal / itemsPerPage) || 1;

    return (
        <AdminLayout>
            {/* Custom ActionBar with integrated search and filter */}
            <div className="px-8 py-6 border-b border-zinc-700">
                <div className="flex justify-between items-center">
                    <h1 className="text-5xl">Transactions</h1>
                    <div className="flex items-center space-x-3">
                        <div className="flex items-center bg-charleston-green border border-zinc-700 rounded-full">
                            {/* Search Input */}
                            <input
                                type="text"
                                placeholder="Search by mobile, UPI, reference ID, account ID..."
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="bg-transparent px-5 py-2.5 text-white placeholder-zinc-500 w-[400px] focus:outline-none"
                            />
                            <svg 
                                className="w-5 h-5 text-zinc-500 mr-3" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        
                        {/* Type Filter */}
                        <div className="relative">
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="flex items-center gap-2 px-4 py-2.5 bg-charleston-green border border-zinc-700 rounded-full text-white hover:bg-zinc-800 transition-colors min-w-[220px]"
                            >
                                <span className="text-sm">
                                    <span className={selectedType ? getTypeColor(selectedType) : ""}>
                                        {TRANSACTION_TYPES.find(t => t.value === selectedType)?.label || "All Types"}
                                    </span>
                                </span>
                                <span className={`transition-transform inline-block ml-auto ${isDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
                            </button>
                            
                            {isDropdownOpen && (
                                <div className="absolute top-full right-0 mt-1 w-56 bg-eerie-black border border-zinc-700 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                                    {TRANSACTION_TYPES.map(type => (
                                        <button
                                            key={type.value}
                                            onClick={() => handleTypeChange(type.value)}
                                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-charleston-green transition-colors ${
                                                selectedType === type.value ? 'bg-charleston-green' : ''
                                            } ${type.value ? getTypeColor(type.value) : 'text-white'}`}
                                        >
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <Container>
                <div className="h-full flex flex-col justify-between">
                    {/* Transactions List */}
                    <div className="flex-1">
                        {transactionsLoading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                            </div>
                        ) : transactions && transactions.length > 0 ? (
                            <div className="space-y-4">
                                {transactions.map((transaction) => (
                                    <TransactionCard 
                                        key={transaction.id || transaction.reference_id || Math.random()} 
                                        transaction={transaction}
                                        onRefresh={() => {
                                            // Refresh current page
                                            const params = { 
                                                page: currentPage, 
                                                size: itemsPerPage 
                                            };
                                            if (selectedType) {
                                                params.type = selectedType;
                                            }
                                            if (searchDebounce.trim()) {
                                                params.search = searchDebounce.trim();
                                            }
                                            dispatch(fetchTransactionsHandler(params));
                                        }}
                                    />
                                ))}
                            </div>
                        ) : searchDebounce && !transactionsLoading ? (
                            <div className="bg-charleston-green rounded-lg p-12 text-center border border-zinc-700">
                                <p className="text-gray-400 text-lg">
                                    No transactions found for {getSearchType(searchDebounce)}: "{searchDebounce}"
                                    {selectedType && ` with type "${TRANSACTION_TYPES.find(t => t.value === selectedType)?.label}"`}
                                </p>
                            </div>
                        ) : !searchDebounce && (!transactions || transactions.length === 0) && !transactionsLoading ? (
                            <div className="bg-charleston-green rounded-lg p-12 text-center border border-zinc-700">
                                <p className="text-gray-400 text-lg">No transactions found</p>
                            </div>
                        ) : null}
                    </div>

                    {/* Pagination */}
                    {!transactionsLoading && transactions && transactions.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={transactionsTotal}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                        />
                    )}
                </div>
            </Container>
        </AdminLayout>
    );
};

export default Transactions;